<template>
  <!-- [연결] — 미배정 전화기를 상담사 번호의 내선에 붙인다(claim) -->
  <a-modal :open="open" :footer="null" :mask-closable="false" destroy-on-close @cancel="close">
    <template #title>
      <div class="modal-title">전화기 연결</div>
    </template>

    <div class="modal-content pbx-dev-claim">
      <template v-if="device">
        <p v-if="formError" class="pbx-dev-claim__error" role="alert">{{ formError }}</p>

        <dl class="pbx-dev-claim__target">
          <div>
            <dt>전화기</dt>
            <dd>{{ deviceTitle }}</dd>
          </div>
          <div v-if="device.model">
            <dt>모델</dt>
            <dd>{{ device.model }}</dd>
          </div>
        </dl>

        <div class="pbx-dev-claim__field">
          <span class="pbx-dev-claim__label">상담사 번호</span>
          <a-select
            v-model:value="counselorNumberId"
            :options="options"
            :loading="inventory.counselorNumbers.loading.value"
            placeholder="상담사 번호 선택"
            show-search
            option-filter-prop="label"
            :not-found-content="listError ? LIST_ERROR_TEXT : '연결할 수 있는 상담사 번호가 없습니다'"
          />
          <!-- 목록을 못 받은 것을 "번호 없음" 으로 보이지 않는다 -->
          <span v-if="listError" class="pbx-dev-claim__hint pbx-dev-claim__hint--error" role="alert">
            {{ LIST_ERROR_TEXT }}
            <a href="#" @click.prevent="retryList">다시 시도</a>
          </span>
          <!-- admin 문구: 내선 발급은 병원이 studio 에서 한다(요청 문서 "요청 계약 3") -->
          <span v-else class="pbx-dev-claim__hint">
            사용 중인 내선이 있는 상담사 번호만 보입니다. 병원이 studio 사용자 관리 › 상담사 번호에서
            [내선번호 사용]을 켜면 연결할 내선이 생깁니다.
          </span>
        </div>
      </template>

      <div class="modal-footer">
        <a-button @click="close">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="saving" :disabled="!canSubmit" @click="submit">
          연결
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { getCallApiErrorBody, getCallApiErrorCode } from '@/views/Callbot/service/callApiError';
import { usePbxAction } from '@/views/Callbot/service/usePbxAction';

import { confirmPbx, deviceName } from './PbxDeviceShared';

const props = defineProps({
  open: { type: Boolean, default: false },
  inventory: { type: Object, required: true },
  device: { type: Object, default: null },
});
const emit = defineEmits(['update:open', 'claimed']);

const { run, isPending } = usePbxAction(props.inventory);

const LIST_ERROR_TEXT = '상담사 번호 목록을 불러오지 못했습니다.';
const listError = computed(() => props.inventory.counselorNumbers.error.value);

function retryList() {
  void props.inventory.reload(['counselorNumbers']);
}

const counselorNumberId = ref(undefined);
const formError = ref('');

const deviceTitle = computed(() => {
  const d = props.device;
  if (!d) return '';
  return d.label ? `${d.label} (${d.mac})` : d.mac;
});

/**
 * 내선에 이미 붙은 전화기 — 요약의 device_id(7단계) 가 없으면 내선 목록에서 찾는다
 * @param {string} extensionId
 * @param {string | null | undefined} summaryDeviceId
 * @returns {string | null}
 */
function linkedDeviceId(extensionId, summaryDeviceId) {
  return summaryDeviceId ?? props.inventory.extensionById.value.get(extensionId)?.device_id ?? null;
}

function linkedDeviceLabel(deviceId) {
  const d = props.inventory.deviceById.value.get(deviceId);
  return d ? deviceName(d) : deviceId;
}

// 사용 중(active) 내선이 붙은 상담사 번호만 — 잠김·일시 중지·회수 내선에는 전화기를 붙일 수 없다
const options = computed(() =>
  props.inventory.counselorNumbers.items.value
    .filter((n) => n.pbx_extension && n.pbx_extension.status === 'active')
    .map((n) => {
      const ext = n.pbx_extension;
      const linked = linkedDeviceId(ext.id, ext.device_id);
      const suffix = linked && linked !== props.device?.id ? ` (전화기 ${linkedDeviceLabel(linked)} 연결됨)` : '';
      return { value: n.id, label: `${n.name} · 내선 ${ext.number}${suffix}` };
    })
);

const saving = computed(() => !!props.device && isPending(`device:claim:${props.device.id}`));
const canSubmit = computed(() => !!props.device && !!counselorNumberId.value && !saving.value);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    counselorNumberId.value = undefined;
    formError.value = '';
  }
);

function close() {
  emit('update:open', false);
}

/**
 * 교체 확인 문구의 기존 전화기 — 고른 번호의 내선 → 오류 details 순으로 찾는다
 * @param {unknown} err
 * @returns {string}
 */
function replacedDeviceLabel(err) {
  const n = counselorNumberId.value
    ? props.inventory.counselorNumberById.value.get(counselorNumberId.value)
    : undefined;
  const ext = n?.pbx_extension;
  const fromExt = ext ? linkedDeviceId(ext.id, ext.device_id) : null;
  const details = getCallApiErrorBody(err)?.error?.details;
  const fromErr = typeof details?.device_id === 'string' ? details.device_id : null;
  const id = fromExt ?? fromErr;
  return id ? linkedDeviceLabel(id) : '';
}

/**
 * @param {boolean} replace
 * @returns {Promise<void>}
 */
async function claim(replace) {
  const device = props.device;
  const cnId = counselorNumberId.value;
  if (!device || !cnId) return;
  formError.value = '';
  // onError 콜백 안에서 채운다
  const after = { replaceLabel: null, close: false };
  const res = await run(
    `device:claim:${device.id}`,
    () =>
      props.inventory.api.claimPbxDevice(device.id, {
        counselor_number_id: cnId,
        ...(replace ? { replace: true } : {}),
      }),
    {
      success: '연결을 완료했습니다.',
      reload: ['devices', 'extensions', 'counselorNumbers'],
      fallback: '전화기를 연결하지 못했습니다.',
      onError: (err, text) => {
        const code = getCallApiErrorCode(err);
        if (code === 'PBX_EXTENSION_ALREADY_ASSIGNED' && !replace) {
          after.replaceLabel = replacedDeviceLabel(err);
          return true;
        }
        // 전화기가 이미 다른 내선에 붙었거나 없어졌다 — 이 모달로는 더 할 게 없다. 공통 토스트 + 재조회
        if (code === 'PBX_DEVICE_ALREADY_ASSIGNED' || code === 'PBX_DEVICE_NOT_FOUND') {
          after.close = true;
          return false;
        }
        formError.value = text;
        return true;
      },
    }
  );
  if (res) {
    emit('claimed', res);
    close();
    return;
  }
  if (after.close) {
    close();
    return;
  }
  if (after.replaceLabel !== null) {
    const labelText = after.replaceLabel ? ` ${after.replaceLabel}` : '';
    const ok = await confirmPbx({
      title: '전화기 교체',
      content: `이 내선에는 이미 전화기${labelText} 가 연결돼 있습니다. 교체할까요? 기존 전화기는 반납 처리되고 내선의 SIP 비밀번호가 회전됩니다.`,
      okText: '교체',
      danger: true,
    });
    if (ok) await claim(true);
  }
}

function submit() {
  if (!canSubmit.value) return;
  void claim(false);
}
</script>

<style scoped lang="scss">
.pbx-dev-claim {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;

  &__error {
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #ffccc7;
    border-radius: 8px;
    background: #fff2f0;
    color: #cf1322;
    font-size: 13px;
    line-height: 1.5;
  }

  &__target {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    div {
      display: flex;
      gap: 12px;
    }

    dt {
      flex: none;
      width: 72px;
      font-size: 13px;
      color: #8c8c8c;
    }

    dd {
      margin: 0;
      font-size: 13px;
      color: #262626;
      word-break: break-all;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
  }

  &__hint {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.5;

    &--error {
      color: #cf1322;
    }
  }

  // admin 모달 규약 — 전역 .modal-content 의 테두리·최소 높이는 antd 모달 안에서 쓰지 않는다
  &.modal-content {
    border: none;
    border-radius: 0;
    min-height: 0;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 0 0;
    border-top: none;
  }
}

.modal-title {
  font-size: 16px;
  font-weight: bold;
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
